import React, { useState } from "react";
import "../index.css";
import "../public/Login.css";
import Footer from "../components/Footer";
import { PageNav } from "../components/PageNav";
import buttonBackground from "../assets/button_background_blue.webp";
import { useAuth } from "../auth/context";
import { useNavigate } from "react-router-dom";
import {
  //cacheProfileSetting,
  getUserProfileSettings,
} from "../auth/UserProfile";
import { BASE_URL } from "../auth/types";
import { auth } from "../auth/firebase";

export default function Register() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { register, loginWithGoogle, loginWithApple } = useAuth();
  const navigate = useNavigate();

  const validateInviteCode = async (code: string): Promise<boolean> => {
    try {
      const response = await fetch(
        `${BASE_URL}/accounts/api/invite/validate/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ invite_code: code }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.valid === false) {
        setError("Invalid invite code. Please check and try again.");
        return false;
      }
      return true;
    } catch {
      setError("Unable to validate invite code. Please try again.");
      return false;
    }
  };

  const handleGoogleRegister = async () => {
    setError("");
    setIsLoading(true);
    let newFirebaseUser = false;

    try {
      const trimmedCode = inviteCode.trim();
      if (trimmedCode) {
        const valid = await validateInviteCode(trimmedCode);
        if (!valid) { setIsLoading(false); return; }
      }
      const user = await loginWithGoogle();
      newFirebaseUser = !!user.isNewUser;
      await getUserProfileSettings(user, "google", "", navigate, trimmedCode || undefined);
    } catch (err) {
      if (newFirebaseUser && auth.currentUser) {
        try { await auth.currentUser.delete(); localStorage.clear(); } catch { /* ignore */ }
      }
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Google authentication failed");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleRegister = async () => {
    setError("");
    setIsLoading(true);
    let newFirebaseUser = false;

    try {
      const trimmedCode = inviteCode.trim();
      if (trimmedCode) {
        const valid = await validateInviteCode(trimmedCode);
        if (!valid) { setIsLoading(false); return; }
      }
      const user = await loginWithApple();
      newFirebaseUser = !!user.isNewUser;
      await getUserProfileSettings(user, "apple", "", navigate, trimmedCode || undefined);
    } catch (err) {
      if (newFirebaseUser && auth.currentUser) {
        try { await auth.currentUser.delete(); localStorage.clear(); } catch { /* ignore */ }
      }
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Apple authentication failed");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate("/login");
  };

  const showError = (msg: string) => {
    setError(msg);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLButtonElement>,
  ): Promise<void> => {
    e.preventDefault();
    setError("");

    // Validation
    if (!firstName.trim()) {
      showError("First name is required");
      return;
    }

    if (!email.trim()) {
      showError("Email is required");
      return;
    }

    if (!password) {
      showError("Password is required");
      return;
    }

    // Password policy validation
    if (password.length < 8) {
      showError("Password must be at least 8 characters long");
      return;
    }

    if (!/[A-Z]/.test(password)) {
      showError("Password must contain at least one uppercase letter");
      return;
    }

    if (!/[a-z]/.test(password)) {
      showError("Password must contain at least one lowercase letter");
      return;
    }

    if (!/\d/.test(password)) {
      showError("Password must contain at least one number");
      return;
    }

    if (password !== confirmPassword) {
      showError("Passwords do not match");
      return;
    }

    setIsLoading(true);
    let firebaseUserCreated = false;

    try {
      const trimmedCode = inviteCode.trim();
      if (trimmedCode) {
        const valid = await validateInviteCode(trimmedCode);
        if (!valid) { setIsLoading(false); return; }
      }

      const user = await register(email, password, firstName);
      firebaseUserCreated = true;

      await getUserProfileSettings(user, "email", firstName, () => {
        navigate("/verify-email");
      }, trimmedCode || undefined);
    } catch (err) {
      if (firebaseUserCreated && auth.currentUser) {
        try { await auth.currentUser.delete(); localStorage.clear(); } catch { /* ignore */ }
      }
      if (err instanceof Error) {
        showError(err.message);
      } else {
        showError("An unexpected error occurred");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-overlay-spinner"></div>
        </div>
      )}
      <PageNav />
      <div className="form-container" style={{ marginTop: 100 }}>
        <div className="form-content">
          <div className="center_me">
            <h2
              className="font_family_base header_text"
              style={{
                fontSize: 28,
                fontWeight: "bold",
                margin: 0,
              }}
            >
              Sign Up
            </h2>
          </div>

          <div
            style={{
              minHeight: "48px",
              marginTop: "10px",
              marginBottom: "10px",
            }}
          >
            {error && (
              <div
                className="font_family_base error_text_color"
                style={{
                  // backgroundColor: "#fee",
                  // color: "#c33",
                  padding: "13px",
                  borderRadius: "8px",
                  textAlign: "center",
                  border: "1px solid #FF0000",
                }}
              >
                {error}
              </div>
            )}
          </div>

          <label className="font_family_base field-name field_name_color">
            First Name: *
          </label>
          <input
            className=" font_family_base input"
            style={{ marginBottom: 20 }}
            type="text"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            disabled={isLoading}
          />

          <label className="font_family_base field-name field_name_color">
            Email: *
          </label>
          <input
            className="font_family_base input"
            style={{ marginBottom: 20 }}
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            disabled={isLoading}
          />

          <label className="font_family_base field-name field_name_color">
            Password: *
          </label>
          <input
            className="font_family_base input"
            style={{ marginBottom: 20 }}
            type="password"
            placeholder="Password (min 8 chars, upper/lower case, number)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            disabled={isLoading}
          />

          <label className="font_family_base field-name field_name_color">
            Confirm Password: *
          </label>
          <input
            className="font_family_base input"
            style={{ marginBottom: 20 }}
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            disabled={isLoading}
          />

          <label className="font_family_base field-name field_name_color">
            Invite Code (optional)
          </label>
          <input
            className="font_family_base input"
            style={{ marginBottom: 20 }}
            type="text"
            placeholder="Invite Code"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            autoComplete="off"
            disabled={isLoading}
          />

          <div
            style={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <button
              className="my-form-button-wide button-base button-3d font_family_base"
              onClick={handleSubmit}
              disabled={isLoading}
              style={{
                marginTop: "5px",
                marginBottom: "15px",
                backgroundImage: `url(${buttonBackground})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                color: "white",
                fontWeight: 700,
              }}
            >
              {isLoading ? (
                <div className="loading-spinner"></div>
              ) : (
                "Register with Email"
              )}
            </button>
          </div>

          <button
            className="google-button font_family_base"
            onClick={handleGoogleRegister}
            disabled={isLoading}
          >
            <span className="google-logo-badge">
              <svg width="16" height="16" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.039l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
              </svg>
            </span>
            Sign up with Google
          </button>

          <button
            className="apple-button font_family_base"
            onClick={handleAppleRegister}
            disabled={isLoading}
          >
            <svg width="16" height="19" viewBox="0 0 17 20" xmlns="http://www.w3.org/2000/svg" fill="white">
              <path d="M14.769 10.679c-.022-2.219 1.814-3.291 1.896-3.344-1.033-1.511-2.641-1.718-3.211-1.738-1.368-.138-2.677.807-3.37.807-.694 0-1.762-.787-2.9-.765-1.486.022-2.86.864-3.626 2.19C2.003 10.587 3.15 14.94 4.663 17.31c.748 1.073 1.636 2.276 2.797 2.233 1.127-.045 1.551-.724 2.913-.724 1.362 0 1.74.724 2.927.701 1.21-.022 1.974-1.094 2.715-2.171.857-1.241 1.209-2.449 1.23-2.511-.027-.012-2.354-.904-2.376-3.159zM12.53 3.6C13.14 2.854 13.55 1.823 13.436.77c-.883.036-1.95.588-2.583 1.334-.567.655-1.063 1.706-.929 2.712.985.076 1.99-.501 2.606-1.216z"/>
            </svg>
            Sign up with Apple
          </button>

          <button
            className={`font_family_base link-text ${
              isLoading ? "link-text-disabled" : ""
            }`}
            onClick={handleLoginRedirect}
            disabled={isLoading}
            type="button"
          >
            Already have an account? Login
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
